import { ArrowBackOutlined, LocalOfferOutlined } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Tab,
    Tabs,
    Typography,
    useTheme,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { JSX, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom';
import { parseApiError } from '../../../library/apiErrorHelper';
import { clinicAdminLink, followUpEvaluationLinkFor, patientsLink } from '../../../library/constants';
import type { IClinicAdminLocationState } from '../ClinicAdmin/ClinicAdmin';
import { memberFullName, WITHDRAWABLE_STATUSES } from '../../../library/membershipDisplay';
import { IPatient, toResendInvitationRequest } from '../../../library/models/clinic';
import { IInvoice, liveInvoiceFor } from '../../../library/models/invoice';
import { IPatientPurchase } from '../../../library/models/patientPurchase';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactMutation, useReactQuery } from '../../../services/queryService';
import { MedicalRecordsService } from '../../../services/medicalRecordsService';
import { services } from '../../../services/services';
import { useAuth } from '../../../contexts/AuthContext';
import { ConfirmationDialog } from '../../confirmationDialog/confirmationDialog';
import { useSnackbar } from '../../layout/snackbarProvider';
import { MembershipStatusChip } from '../../membershipStatusChip/membershipStatusChip';
import { PatientTherapists } from '../../patientTherapists/patientTherapists';
import { AddPurchaseDialog } from './AddPurchaseDialog';
import { MedicalRecordsTimeline } from './MedicalRecordsTimeline';
import { PurchaseCard } from './PurchaseCard';
import { UploadDocumentsDialog } from './UploadDocumentsDialog';
import { UserAvatar } from '../../userAvatar/UserAvatar';
import {
    alertStyles,
    backButtonStyles,
    cardListStyles,
    emptyStateBoxStyles,
    emptyStateIconStyles,
    headerBoxStyles,
    identifierStyles,
    identityRowStyles,
    loadingBoxStyles,
    sectionHeaderActionsStyles,
    sectionHeaderStyles,
    sectionStyles,
    sectionTitleStyles,
    subtitleStyles,
    tabsStyles,
    titleRowStyles,
    titleStyles,
} from './patientDetail.styles';

type PurchaseTab = 'ACTIVE' | 'HISTORY';

/**
 * One patient's file, as a therapist works from it: what they have bought, how much of it
 * is left, and their medical record -- the evaluations taken here and the documents they
 * brought in.
 *
 * The patient's own details come from the clinic's patient list rather than a lookup of
 * their own. That list is already cached by the page this one is opened from, it is small
 * enough to hold, and reusing it means no second endpoint to tenant-scope. A deep link
 * with a cold cache simply fetches the list first.
 */
export const PatientDetail = (): JSX.Element => {
    const theme = useTheme();
    const navigate: NavigateFunction = useNavigate();
    const { t: translate } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const { patientId: patientIdParam } = useParams<{ patientId: string }>();
    /** Only to mark the caller's own chip among this patient's therapists. */
    const { user, role } = useAuth();

    /**
     * An administrator reaches this page from My Clinic rather than from the patient list,
     * which is a therapist's page they cannot open -- so "back" returns them to the tab they
     * came from.
     */
    const isAdmin = role === 'ADMIN';
    const goBack = (): void => {
        if (isAdmin) {
            const state: IClinicAdminLocationState = { tab: 'PATIENTS' };
            navigate(clinicAdminLink, { state });
        } else {
            navigate(patientsLink);
        }
    };

    const queryClient = useQueryClient();

    const [isAddOpen, setAddOpen] = useState(false);
    const [isUploadOpen, setUploadOpen] = useState(false);
    const [purchaseTab, setPurchaseTab] = useState<PurchaseTab>('ACTIVE');
    /** Whether the resend confirmation is open. One patient here, so a boolean will do. */
    const [isResendOpen, setResendOpen] = useState(false);
    const [isWithdrawOpen, setWithdrawOpen] = useState(false);

    const patientId = Number(patientIdParam);

    const {
        data: patient,
        isLoading: isLoadingPatient,
        refetch: refetchPatient,
    } = useReactQuery<IPatient>(
        services.users.getPatient(patientId),
        // A non-numeric id in the URL would otherwise become "/users/patients/NaN".
        { enabled: Number.isFinite(patientId) }
    );

    const inviteCustomer = useReactMutation(services.users.inviteCustomerMutation());
    const withdrawInvitation = useReactMutation(services.users.withdrawInvitationMutation());

    const {
        data: purchaseData,
        isLoading: isLoadingPurchases,
        refetch: refetchPurchases,
    } = useReactQuery<IPatientPurchase[]>(
        services.patientPurchases.getPurchasesForPatient(patientId),
        // A non-numeric id in the URL would otherwise become "?patientId=NaN".
        { enabled: Number.isFinite(patientId) }
    );

    /**
     * Every invoice for this patient, in one request rather than one per purchase card.
     * Cancelled ones come back too, and `liveInvoiceFor` is what picks the one that counts.
     */
    const { data: invoiceData, refetch: refetchInvoices } = useReactQuery<IInvoice[]>(
        services.invoicing.getInvoicesForPatient(patientId),
        { enabled: Number.isFinite(patientId) }
    );

    const invoices = useMemo(() => invoiceData ?? [], [invoiceData]);


    // The same distinction the patient list draws: a query can settle without data and
    // without an error, and reading that as "nothing bought" would be a lie.
    const hasLoadedPurchases = purchaseData !== undefined;
    const purchases = useMemo(() => purchaseData ?? [], [purchaseData]);

    /**
     * What is still running, and what is done with. Split on the status the server
     * derived rather than re-deriving it here, so the card, the chip and this grouping
     * can never disagree about whether a package is still usable.
     */
    const active = useMemo(
        () => purchases.filter(purchase => purchase.status === 'ACTIVE' || purchase.status === 'EXPIRED'),
        [purchases]
    );
    const history = useMemo(
        () => purchases.filter(purchase => purchase.status === 'COMPLETED' || purchase.status === 'CANCELLED'),
        [purchases]
    );

    if (isLoadingPatient) {
        return (
            <Box sx={loadingBoxStyles}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (!patient) {
        return (
            <Box>
                <Button
                    startIcon={<ArrowBackOutlined />}
                    sx={backButtonStyles}
                    onClick={goBack}
                >
                    {translate(ResourcesIds.PATIENT_DETAIL_BACK)}
                </Button>

                <Alert severity="error" sx={alertStyles}>
                    {translate(ResourcesIds.PATIENT_DETAIL_NOT_FOUND)}
                </Alert>
            </Box>
        );
    }

    /**
     * Sends the invitation again: the same email, with the same link. The patient is
     * refetched afterwards so the chip shows what the server now says, though for a
     * pending patient it does not change -- the snackbar is the proof the mail went.
     */
    const confirmResend = (): void => {
        setResendOpen(false);

        inviteCustomer.mutate(toResendInvitationRequest(patient), {
            onSuccess: () => {
                showSnackbar(
                    translate(ResourcesIds.PATIENTS_RESEND_INVITATION_SUCCESS, {
                        emailAddress: patient.emailAddress,
                    }),
                    'success'
                );
                refetchPatient();
            },
            onError: (error: unknown) => {
                const parsed = parseApiError(error);
                showSnackbar(
                    parsed.message ?? translate(ResourcesIds.PATIENTS_RESEND_INVITATION_ERROR),
                    'error'
                );
            },
        });
    };

    /**
     * Takes the invitation back, which is what stops its link: a patient's never expires,
     * so one sent to the wrong address works until somebody does this. The patient is
     * refetched because the chip is the proof -- it goes from "invitation sent" to
     * "access revoked", and this button goes with it.
     */
    const confirmWithdraw = (): void => {
        setWithdrawOpen(false);

        withdrawInvitation.mutate(patient.id, {
            onSuccess: () => {
                showSnackbar(
                    translate(ResourcesIds.PATIENTS_WITHDRAW_INVITATION_SUCCESS),
                    'success'
                );
                refetchPatient();
            },
            onError: (error: unknown) => {
                const parsed = parseApiError(error);
                showSnackbar(
                    parsed.message ?? translate(ResourcesIds.PATIENTS_WITHDRAW_INVITATION_ERROR),
                    'error'
                );
                // The usual refusal is that they accepted in the meantime, and then the
                // chip on screen is what is out of date.
                refetchPatient();
            },
        });
    };

    const canWithdraw: boolean = WITHDRAWABLE_STATUSES.includes(patient.status);

    return (
        <Box>
            <Button
                startIcon={<ArrowBackOutlined />}
                sx={backButtonStyles}
                onClick={goBack}
            >
                {translate(ResourcesIds.PATIENT_DETAIL_BACK)}
            </Button>

            <Box sx={headerBoxStyles}>
                <Box>
                    <Box sx={titleRowStyles}>
                        <UserAvatar person={patient} size="lg" />

                        <Typography variant="h4" component="h1" sx={titleStyles}>
                            {memberFullName(patient)}
                        </Typography>
                    </Box>

                    <Box sx={identityRowStyles}>
                        <Typography component="span" sx={identifierStyles}>
                            {patient.emailAddress}
                        </Typography>
                        <Typography component="span" sx={identifierStyles}>
                            {patient.phoneNumber ?? translate(ResourcesIds.PHONE_NOT_GIVEN)}
                        </Typography>
                        <MembershipStatusChip
                            status={patient.status}
                            onResend={() => setResendOpen(true)}
                        />
                        {canWithdraw && (
                            <Button
                                size="small"
                                color="error"
                                disabled={withdrawInvitation.isPending}
                                onClick={() => setWithdrawOpen(true)}
                            >
                                {translate(ResourcesIds.PATIENTS_WITHDRAW_INVITATION_ACTION)}
                            </Button>
                        )}
                    </Box>

                    {/* Who this patient belongs to, on the page where their treatment is
                        actually read -- a therapist opening a colleague's patient should
                        see whose they are before recording anything against them. */}
                    <Box sx={identityRowStyles}>
                        <PatientTherapists
                            therapists={patient.therapists}
                            currentUserId={user?.id}
                        />
                    </Box>
                </Box>
            </Box>

            <Box sx={sectionStyles}>
                <Box sx={sectionHeaderStyles}>
                    <Box>
                        <Typography variant="h5" sx={sectionTitleStyles}>
                            {translate(ResourcesIds.PATIENT_PURCHASES_TITLE)}
                        </Typography>
                        <Typography variant="body2" sx={subtitleStyles}>
                            {translate(ResourcesIds.PATIENT_PURCHASES_SUBTITLE)}
                        </Typography>
                    </Box>

                    <Button variant="contained" onClick={() => setAddOpen(true)}>
                        {translate(ResourcesIds.PATIENT_PURCHASES_ADD)}
                    </Button>
                </Box>

                {!isLoadingPurchases && !hasLoadedPurchases && (
                    <Alert severity="error" sx={alertStyles}>
                        {translate(ResourcesIds.PATIENT_PURCHASES_LOAD_ERROR)}
                    </Alert>
                )}

                {isLoadingPurchases ? (
                    <Box sx={loadingBoxStyles}>
                        <CircularProgress size={40} />
                    </Box>
                ) : !hasLoadedPurchases ? null : purchases.length === 0 ? (
                    <Box sx={emptyStateBoxStyles}>
                        <LocalOfferOutlined sx={emptyStateIconStyles(theme)} />
                        <Typography variant="h6" gutterBottom>
                            {translate(ResourcesIds.PATIENT_PURCHASES_EMPTY_TITLE)}
                        </Typography>
                        <Typography variant="body2" sx={subtitleStyles}>
                            {translate(ResourcesIds.PATIENT_PURCHASES_EMPTY_SUBTITLE)}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Tabs
                            value={purchaseTab}
                            onChange={(_event, next: PurchaseTab) => setPurchaseTab(next)}
                            sx={tabsStyles}
                        >
                            <Tab
                                value="ACTIVE"
                                label={translate(ResourcesIds.PATIENT_PURCHASES_ACTIVE)}
                            />
                            <Tab
                                value="HISTORY"
                                label={translate(ResourcesIds.PATIENT_PURCHASES_HISTORY)}
                            />
                        </Tabs>

                        {/* The data for both tabs is already in hand from the one fetch
                            above -- only which list gets rendered is deferred to a click. */}
                        {(purchaseTab === 'ACTIVE' ? active : history).length === 0 ? (
                            <Box sx={emptyStateBoxStyles}>
                                <LocalOfferOutlined sx={emptyStateIconStyles(theme)} />
                                <Typography variant="body2" sx={subtitleStyles}>
                                    {translate(ResourcesIds.PATIENT_PURCHASES_TAB_EMPTY)}
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={cardListStyles}>
                                {(purchaseTab === 'ACTIVE' ? active : history).map(purchase => (
                                    <PurchaseCard
                                        key={purchase.id}
                                        purchase={purchase}
                                        onChanged={refetchPurchases}
                                        invoice={liveInvoiceFor(purchase, invoices)}
                                        patientName={memberFullName(patient)}
                                        patientEmail={patient.emailAddress}
                                        onInvoiceChanged={refetchInvoices}
                                        canAdjustUsedBefore={isAdmin}
                                    />
                                ))}
                            </Box>
                        )}
                    </>
                )}
            </Box>

            <Box sx={sectionStyles}>
                <Box sx={sectionHeaderStyles}>
                    <Box>
                        <Typography variant="h5" sx={sectionTitleStyles}>
                            {translate(ResourcesIds.PATIENT_DETAIL_MEDICAL_RECORDS_TITLE)}
                        </Typography>
                        <Typography variant="body2" sx={subtitleStyles}>
                            {translate(ResourcesIds.PATIENT_DETAIL_MEDICAL_RECORDS_SUBTITLE)}
                        </Typography>
                    </Box>

                    <Box sx={sectionHeaderActionsStyles}>
                        {/* For everybody who can open this page -- a therapist and an
                            administrator alike. Filing what a patient hands over at the desk
                            is front-desk work as much as it is a therapist's. */}
                        <Button variant="outlined" onClick={() => setUploadOpen(true)}>
                            {translate(ResourcesIds.UPLOAD_DOCUMENTS_BUTTON)}
                        </Button>

                        {/* A sheet is never edited, so recording a follow-up is how anything
                            about this patient changes. Started from here rather than from the
                            patients list, which is where it used to be: opening a blank form
                            and retyping their address is how a returning patient ends up with
                            a second account. */}
                        {/* Not for an administrator: the intake wizard is a therapist's screen,
                            and the sheet it produces is signed in their name. */}
                        {!isAdmin && (
                            <Button
                                variant="contained"
                                onClick={() => navigate(followUpEvaluationLinkFor(patient.id))}
                            >
                                {translate(ResourcesIds.EVALUATION_NEW)}
                            </Button>
                        )}
                    </Box>
                </Box>

                <MedicalRecordsTimeline patientId={patient.id} />
            </Box>

            <UploadDocumentsDialog
                open={isUploadOpen}
                patientId={patient.id}
                onClose={() => setUploadOpen(false)}
                // Every page of the list, not just the first: a document dated last March
                // lands wherever March is, which may be a page that is already on screen.
                onUploaded={() => {
                    void queryClient.invalidateQueries({ queryKey: [MedicalRecordsService.TIMELINE_URL] });
                }}
            />

            <AddPurchaseDialog
                open={isAddOpen}
                patientId={patient.id}
                onClose={() => setAddOpen(false)}
                onAdded={() => { void refetchPurchases(); }}
            />

            <ConfirmationDialog
                isOpen={isResendOpen}
                onClose={() => setResendOpen(false)}
                onConfirm={confirmResend}
                title={translate(ResourcesIds.PATIENTS_RESEND_INVITATION_TITLE)}
                description={translate(ResourcesIds.PATIENTS_RESEND_INVITATION_DESCRIPTION)}
                confirmMessage={translate(ResourcesIds.PATIENTS_RESEND_INVITATION_CONFIRM)}
                denyMessage={translate(ResourcesIds.CANCEL_TEXT)}
                confirmColor="primary"
            />

            <ConfirmationDialog
                isOpen={isWithdrawOpen}
                onClose={() => setWithdrawOpen(false)}
                onConfirm={confirmWithdraw}
                title={translate(ResourcesIds.PATIENTS_WITHDRAW_INVITATION_TITLE)}
                description={translate(ResourcesIds.PATIENTS_WITHDRAW_INVITATION_DESCRIPTION, {
                    emailAddress: patient.emailAddress,
                })}
                confirmMessage={translate(ResourcesIds.PATIENTS_WITHDRAW_INVITATION_CONFIRM)}
                denyMessage={translate(ResourcesIds.CANCEL_TEXT)}
            />
        </Box>
    );
};
